import { z } from 'zod';
import { CodFormStyleSchema, DEFAULT_COD_FORM_STYLE } from './cod-form-style.schema';

// The built-in checkout fields, in the order the storefront renders them by default.
export const COD_BUILTIN_KEYS = [
  'fullName', 'firstName', 'lastName', 'phone', 'email',
  'address1', 'address2', 'city', 'region', 'postalCode', 'country', 'notes'
] as const;

export type CodBuiltinKey = (typeof COD_BUILTIN_KEYS)[number];

// Fields backing a NOT NULL order column with no sane default; a store may rename them but never hide them.
export const COD_UNHIDEABLE_KEYS: CodBuiltinKey[] = ['phone', 'city', 'address1'];

// How many custom fields one store may add; enforced server-side on every write.
export const COD_CUSTOM_FIELD_LIMIT = 10;

// A field key is a storage contract: lowercase, snake-cased, and never derived from the label at read time.
const COD_KEY_RE = /^[a-z][a-z0-9_]{1,39}$/;

// Keys that resolve on Object.prototype. The readers build prototype-less bags; this refuses them at the door too.
const COD_RESERVED_KEYS = ['constructor', 'prototype', 'proto', 'tostring', 'valueof', 'hasownproperty'];

export const CodVisibilitySchema = z.enum(['HIDDEN', 'OPTIONAL', 'REQUIRED']);
export const CodNameModeSchema = z.enum(['FULL_NAME', 'SPLIT_NAME']);
export const CodCustomTypeSchema = z.enum(['TEXT', 'NUMBER', 'TEXTAREA', 'SELECT', 'CHECKBOX']);

export type CodVisibility = z.infer<typeof CodVisibilitySchema>;
export type CodNameMode = z.infer<typeof CodNameModeSchema>;
export type CodCustomType = z.infer<typeof CodCustomTypeSchema>;

// One built-in field: the merchant owns its presentation, never its storage key.
export const CodBuiltinFieldSchema = z
  .object({
    visibility: CodVisibilitySchema,
    label: z.string().trim().min(1, 'A field label cannot be empty').max(60),
    placeholder: z.string().trim().max(120).default('')
  })
  .strict();

// One choice offered by a SELECT custom field; the value is stored, the label only shown.
export const CodSelectOptionSchema = z
  .object({
    value: z.string().trim().min(1).max(60),
    label: z.string().trim().min(1).max(60)
  })
  .strict();

// One merchant-defined field; `key` names its database slot and its courier-export column.
export const CodCustomFieldSchema = z
  .object({
    key: z.string().trim().regex(COD_KEY_RE, 'A field key must start with a letter and hold only lowercase letters, digits and underscores'),
    label: z.string().trim().min(1, 'A field label cannot be empty').max(60),
    placeholder: z.string().trim().max(120).default(''),
    type: CodCustomTypeSchema,
    required: z.boolean().default(false),
    position: z.number().int().min(0).max(99).default(0),
    options: z.array(CodSelectOptionSchema).max(20).default([])
  })
  .strict()
  .superRefine((field, ctx) => {
    if ((COD_BUILTIN_KEYS as readonly string[]).includes(field.key)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['key'], message: `"${field.key}" is a built-in field key and cannot be reused` });
    }
    if (COD_RESERVED_KEYS.includes(field.key.toLowerCase())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['key'], message: `"${field.key}" is a reserved key and cannot be used` });
    }
    if (field.type === 'SELECT' && field.options.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['options'], message: 'A dropdown field needs at least one option' });
    }
    if (field.type !== 'SELECT' && field.options.length > 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['options'], message: 'Only a dropdown field carries options' });
    }
  });

export type CodBuiltinField = z.infer<typeof CodBuiltinFieldSchema>;
export type CodSelectOption = z.infer<typeof CodSelectOptionSchema>;
export type CodCustomField = z.infer<typeof CodCustomFieldSchema>;

// Every built-in key is always present, so a stored config is complete rather than partially defaulted at read time.
const builtinShape = Object.fromEntries(
  COD_BUILTIN_KEYS.map((key) => [key, CodBuiltinFieldSchema])
) as Record<CodBuiltinKey, typeof CodBuiltinFieldSchema>;

export const CodBuiltinFieldsSchema = z.object(builtinShape).strict();

// Rejects a configuration that would leave the checkout unable to fill a NOT NULL order column.
function assertConfigIsSatisfiable(
  config: { nameMode: CodNameMode; fields: Record<CodBuiltinKey, CodBuiltinField>; customFields: CodCustomField[] },
  ctx: z.RefinementCtx
): void {
  for (const key of COD_UNHIDEABLE_KEYS) {
    if (config.fields[key].visibility !== 'REQUIRED') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fields', key, 'visibility'], message: `"${config.fields[key].label}" has to stay required — every order needs it to ship` });
    }
  }
  assertNameModeIsCoherent(config, ctx);
  const seen = new Set<string>();
  for (const [index, field] of config.customFields.entries()) {
    if (seen.has(field.key)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['customFields', index, 'key'], message: `The field key "${field.key}" is already used` });
    }
    seen.add(field.key);
  }
}

// Rejects a name mode whose halves contradict it, so exactly one naming shape is ever active.
function assertNameModeIsCoherent(
  config: { nameMode: CodNameMode; fields: Record<CodBuiltinKey, CodBuiltinField> },
  ctx: z.RefinementCtx
): void {
  const { fullName, firstName, lastName } = config.fields;
  if (config.nameMode === 'FULL_NAME') {
    if (fullName.visibility !== 'REQUIRED') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fields', 'fullName', 'visibility'], message: 'One name field has to stay required' });
    }
    if (firstName.visibility !== 'HIDDEN' || lastName.visibility !== 'HIDDEN') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nameMode'], message: 'A single name field cannot run alongside the split pair' });
    }
    return;
  }
  if (fullName.visibility !== 'HIDDEN') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nameMode'], message: 'The split pair cannot run alongside a single name field' });
  }
  if (firstName.visibility === 'HIDDEN' || lastName.visibility === 'HIDDEN') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nameMode'], message: 'The split pair shows both halves; hide one by switching back to a single name field' });
  }
  if (firstName.visibility !== 'REQUIRED' && lastName.visibility !== 'REQUIRED') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fields', 'firstName', 'visibility'], message: 'At least one half of the name has to stay required' });
  }
}

// Where the shopper meets the COD form: inline under the product, in a modal, or on its own page.
export const CodCheckoutDisplaySchema = z.enum(['EMBEDDED', 'POPUP', 'ONE_PAGE']);

export type CodCheckoutDisplay = z.infer<typeof CodCheckoutDisplaySchema>;

// A store's whole COD form: where it is shown, the naming shape, the built-in fields and the merchant's own.
export const CodFormConfigSchema = z
  .object({
    checkoutDisplay: CodCheckoutDisplaySchema.default('EMBEDDED'),
    nameMode: CodNameModeSchema,
    fields: CodBuiltinFieldsSchema,
    customFields: z.array(CodCustomFieldSchema).max(COD_CUSTOM_FIELD_LIMIT, `A store may add at most ${COD_CUSTOM_FIELD_LIMIT} custom fields`),
    style: CodFormStyleSchema.default({}),
    version: z.number().int().nonnegative().default(0)
  })
  .strict()
  .superRefine(assertConfigIsSatisfiable);

export type CodFormConfig = z.infer<typeof CodFormConfigSchema>;

// The write payload; the version is stamped by the server, never chosen by the client.
export const CodFormConfigInputSchema = z
  .object({
    checkoutDisplay: CodCheckoutDisplaySchema.default('EMBEDDED'),
    nameMode: CodNameModeSchema,
    fields: CodBuiltinFieldsSchema,
    customFields: z.array(CodCustomFieldSchema).max(COD_CUSTOM_FIELD_LIMIT, `A store may add at most ${COD_CUSTOM_FIELD_LIMIT} custom fields`),
    style: CodFormStyleSchema.default({})
  })
  .strict()
  .superRefine(assertConfigIsSatisfiable);

export type CodFormConfigInput = z.infer<typeof CodFormConfigInputSchema>;

// One answer as it arrives from the storefront, before the config decides what it means.
export const CodFieldValueSchema = z.union([z.string().max(500), z.number(), z.boolean()]);

export type CodFieldValue = z.infer<typeof CodFieldValueSchema>;

// The submitted answers, keyed by field key; the cap bounds the body a stranger can post.
export const CodFieldValuesSchema = z
  .record(z.string().max(40), CodFieldValueSchema)
  .refine((values) => Object.keys(values).length <= COD_BUILTIN_KEYS.length + COD_CUSTOM_FIELD_LIMIT, {
    message: 'Too many checkout fields were submitted'
  });

// What a field was called and how it behaved when an order was placed, stored beside the answers.
export const CodSubmittedFieldSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    type: z.string(),
    required: z.boolean(),
    builtin: z.boolean()
  })
  .strict();

export type CodSubmittedField = z.infer<typeof CodSubmittedFieldSchema>;

// One field ready to render: the same shape whether it is built in or the merchant's own.
export interface CodRenderField {
  key: string;
  label: string;
  placeholder: string;
  type: CodCustomType;
  required: boolean;
  builtin: boolean;
  options: CodSelectOption[];
}

// The one render order the storefront, the admin preview and the server validator all read.
export function visibleCodFields(config: CodFormConfig): CodRenderField[] {
  const builtins: CodRenderField[] = COD_BUILTIN_KEYS
    .filter((key) => config.fields[key].visibility !== 'HIDDEN')
    .map((key) => ({
      key,
      label: config.fields[key].label,
      placeholder: config.fields[key].placeholder,
      type: key === 'notes' ? 'TEXTAREA' : 'TEXT',
      required: config.fields[key].visibility === 'REQUIRED',
      builtin: true,
      options: []
    }));
  const custom: CodRenderField[] = [...config.customFields]
    .sort((a, b) => a.position - b.position)
    .map((field) => ({
      key: field.key,
      label: field.label,
      placeholder: field.placeholder,
      type: field.type,
      required: field.required,
      builtin: false,
      options: field.options
    }));
  return [...builtins, ...custom];
}

// Reduces a render field to the presentation snapshot an order keeps for good.
export const toCodSubmittedField = (field: CodRenderField): CodSubmittedField =>
  ({ key: field.key, label: field.label, type: field.type, required: field.required, builtin: field.builtin });

// What a store that never opened the builder collects: the Moroccan COD minimum, plus a courier note.
export const DEFAULT_COD_FORM_CONFIG: CodFormConfig = {
  checkoutDisplay: 'EMBEDDED',
  nameMode: 'FULL_NAME',
  fields: {
    fullName: { visibility: 'REQUIRED', label: 'الاسم الكامل', placeholder: 'الاسم العائلي والشخصي' },
    firstName: { visibility: 'HIDDEN', label: 'الاسم الشخصي', placeholder: '' },
    lastName: { visibility: 'HIDDEN', label: 'الاسم العائلي', placeholder: '' },
    phone: { visibility: 'REQUIRED', label: 'رقم الهاتف (الواتساب)', placeholder: '06 12 34 56 78' },
    email: { visibility: 'HIDDEN', label: 'البريد الإلكتروني', placeholder: 'you@example.com' },
    address1: { visibility: 'REQUIRED', label: 'عنوان التوصيل', placeholder: 'الحي، اسم الشارع، رقم المنزل' },
    address2: { visibility: 'HIDDEN', label: 'تكملة العنوان', placeholder: '' },
    city: { visibility: 'REQUIRED', label: 'المدينة', placeholder: 'الدار البيضاء، الرباط، مراكش...' },
    region: { visibility: 'HIDDEN', label: 'الجهة', placeholder: '' },
    postalCode: { visibility: 'HIDDEN', label: 'الرمز البريدي', placeholder: '' },
    country: { visibility: 'HIDDEN', label: 'البلد', placeholder: '' },
    notes: { visibility: 'OPTIONAL', label: 'ملاحظات للتوصيل', placeholder: 'الاتصال قبل المجيء' }
  },
  customFields: [],
  style: DEFAULT_COD_FORM_STYLE,
  version: 0
};

// Turns a merchant's label into a candidate storage key; only ever a suggestion, never applied on its own.
export function suggestCodFieldKey(label: string): string {
  const slug = label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  if (slug === '' || !/^[a-z]/.test(slug)) return `field_${slug}`.slice(0, 40).replace(/_+$/, '');
  return slug;
}
